import { Wand2 } from "lucide-react";
import { lazy, Suspense, useState } from "react";
import { Button, IconButton, type ButtonProps } from "@/components/ui/button";
import { useT } from "@/lib/i18n";
import type { MediaAsset } from "@/lib/types";

/** The editor (Konva) is code-split: it loads the first time someone opens it. */
const LazyEditor = lazy(() => import("./editor").then((m) => ({ default: m.MediaEditorDialog })));

export type ExportedMedia = { id: string; url: string; mime: string; version: number };

/**
 * "Edit" entry point used by AI Studio results, the media library, the composer and job results. Images only.
 * `iconOnly` renders a compact icon button (thumbnails); otherwise a labelled button.
 */
export function EditImageButton({
  media, onExported, iconOnly, size = "sm", variant = "outline", className, testId = "edit-image",
}: {
  media: Pick<MediaAsset, "id" | "url" | "mime"> & Partial<MediaAsset>;
  onExported?: (m: ExportedMedia) => void;
  iconOnly?: boolean;
  size?: ButtonProps["size"];
  variant?: ButtonProps["variant"];
  className?: string;
  testId?: string;
}) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  if (!media.url || !media.mime.startsWith("image/")) return null;
  const asset: MediaAsset = { width: null, height: null, durationSec: null, ...media } as MediaAsset;
  return (
    <>
      {iconOnly ? (
        <IconButton size={size === "md" ? "icon" : "icon-xs"} variant={variant} label={t("mediaEditor.openLabel")} className={className} onClick={() => setOpen(true)} data-testid={testId}>
          <Wand2 />
        </IconButton>
      ) : (
        <Button size={size} variant={variant} icon={<Wand2 />} className={className} onClick={() => setOpen(true)} data-testid={testId}>
          {t("mediaEditor.open")}
        </Button>
      )}
      {open && (
        <Suspense fallback={null}>
          <LazyEditor media={asset} open={open} onOpenChange={setOpen} onExported={onExported} />
        </Suspense>
      )}
    </>
  );
}
